/**
 * Adaptateur - ConsoleAuditLogger
 *
 * Logger d'audit stockant dans localStorage (append-only, immutable)
 * En production, ceci irait vers un système de journalisation sécurisé et centralisé
 */

import {
  IAuditLogger,
  WithdrawalAuditEntry,
  DepositAuditEntry,
  AuditEntry,
} from '../../application/ports/output/IAuditLogger';

const STORAGE_KEY = 'biotransac_audit_log';

interface StoredAuditEntry {
  type: 'WITHDRAWAL' | 'DEPOSIT';
  data: WithdrawalAuditEntry | DepositAuditEntry;
  loggedAt: string; // ISO string
}

export class ConsoleAuditLogger implements IAuditLogger {
  /**
   * Enregistre un retrait dans l'audit trail
   */
  async logWithdrawal(entry: WithdrawalAuditEntry): Promise<void> {
    const auditEntry: StoredAuditEntry = {
      type: 'WITHDRAWAL',
      data: entry,
      loggedAt: new Date().toISOString(),
    };

    this.appendToLog(auditEntry);

    console.log('[AUDIT] Retrait enregistré:', {
      transactionId: entry.transactionId,
      accountNumber: entry.accountNumber,
      amount: entry.amount,
      clients: entry.authenticatedClients.map(c => c.clientName).join(', '),
      destination: entry.destination,
    });
  }

  /**
   * Enregistre un versement dans l'audit trail
   */
  async logDeposit(entry: DepositAuditEntry): Promise<void> {
    const auditEntry: StoredAuditEntry = {
      type: 'DEPOSIT',
      data: entry,
      loggedAt: new Date().toISOString(),
    };

    this.appendToLog(auditEntry);

    console.log('[AUDIT] Versement enregistré:', {
      transactionId: entry.transactionId,
      accountNumber: entry.accountNumber,
      amount: entry.amount,
      depositor: entry.depositorInfo.isThirdParty
        ? entry.depositorInfo.thirdParty?.name
        : entry.depositorInfo.clientName,
    });
  }

  /**
   * Récupère l'audit trail dans une plage de dates
   */
  async getAuditTrail(fromDate: Date, toDate: Date): Promise<AuditEntry[]> {
    const log = this.getLog();
    return log
      .filter((entry) => {
        const entryDate = new Date(entry.data.timestamp);
        return entryDate >= fromDate && entryDate <= toDate;
      })
      .map((entry) => entry.data);
  }

  /**
   * Récupère toutes les entrées d'audit (avec pagination)
   */
  async getAllEntries(offset = 0, limit = 100): Promise<AuditEntry[]> {
    const log = this.getLog();
    return log.slice(offset, offset + limit).map((entry) => entry.data);
  }

  /**
   * Compte le nombre total d'entrées d'audit
   */
  async count(): Promise<number> {
    const log = this.getLog();
    return log.length;
  }

  /**
   * Ajoute une entrée au log (append-only)
   */
  private appendToLog(entry: StoredAuditEntry): void {
    const log = this.getLog();
    log.push(entry);

    // Append-only: on ne modifie JAMAIS les entrées existantes
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(log));
    } catch (error) {
      console.error('[AUDIT] Erreur lors de la sauvegarde:', error);
      // En production, ceci devrait lever une alerte critique
      throw new Error('Échec de la journalisation d\'audit');
    }
  }

  /**
   * Récupère le log complet
   */
  private getLog(): StoredAuditEntry[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('[AUDIT] Erreur lors du chargement du log:', error);
      return [];
    }
  }

  /**
   * Supprime tout le log (pour tests uniquement - à ne JAMAIS utiliser en production)
   */
  async clearAll(): Promise<void> {
    console.warn('[AUDIT] ATTENTION: Suppression du log d\'audit (tests uniquement)');
    localStorage.removeItem(STORAGE_KEY);
  }
}
